import React, { useState } from "react";

import { CustomSwitch } from "src/app/components/CustomSwitch/CustomSwitch";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import { ProjectPermissionsEnum } from "src/utils/PermissionsEnums";
import { wsAxiosPost, wsAxiosPut } from "src/helpers/wsAxios";
import { API_URLS } from "src/utils/API_URLS";
import { useDispatch } from "react-redux";
import { WsExtension } from "src/types";
import { getExtensionList, setPlanUpgradeNeededModalContext } from "src/redux";
import ExtensionsListItemContent from "./ExtensionsListItemContent";
import { SubscriptionEntitlements } from "../../../../../subscription/subscriptionUtils";

interface ExtensionListStorePublishedContentProps {
  extensionData: WsExtension | undefined;
  permissions: { project: string[]; workspace: string[] };
  wsProjectUuid: string | null;
  hasSubscriptionAccessToStorePublishedContentsExtension: boolean;
}

const changeWsExtensionStatus = async (
  wsExtensionUuid: string,
  enabled: boolean,
) => {
  await wsAxiosPut(
    API_URLS.updateExtensionStatus.replace(
      ":wsExtensionUuid:",
      wsExtensionUuid,
    ),
    {
      enabled,
    },
    `Cannot change status`,
  );
};

const createStorePublishedContentExtension = async (
  wsProjectUuid: string | null,
) => {
  if (!wsProjectUuid) {
    console.error(
      "Missing wsProjectUuid to create extension, got: ",
      wsProjectUuid,
    );

    return;
  }

  await wsAxiosPost(
    API_URLS.createNewStorePublishedContentsExtension,
    {
      wsProjectUuid,
    },
    `Cannot creating extension`,
  );
};

const existsExtension = (extensionData: WsExtension | undefined): boolean => {
  return !!extensionData;
};

export default function ExtensionListStorePublishedContent({
  extensionData,
  permissions,
  wsProjectUuid,
  hasSubscriptionAccessToStorePublishedContentsExtension,
}: ExtensionListStorePublishedContentProps) {
  const [toggled, setToggled] = useState(
    existsExtension(extensionData) ? extensionData?.enabled : false,
  );
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  return (
    <ExtensionsListItemContent
      dataQa="ws_store_published_contents"
      name={
        <IDHFormattedMessage
          id="ws_store_published_contents"
          defaultMessage="Store published contents"
        />
      }
      description={
        <IDHFormattedMessage
          id="ws_store_published_contents_description"
          defaultMessage="Extension to store published contents"
        />
      }
      controls={
        <CustomSwitch
          checked={toggled}
          onChange={async (e) => {
            if (existsExtension(extensionData) && extensionData?.uuid) {
              if (hasSubscriptionAccessToStorePublishedContentsExtension) {
                setToggled(e.target.checked);
                setLoading(true);

                setTimeout(() => {
                  changeWsExtensionStatus(extensionData?.uuid, !toggled);
                  setLoading(false);
                }, 200);
              } else {
                dispatch(
                  setPlanUpgradeNeededModalContext(
                    SubscriptionEntitlements.StorePublishedContentsExtension,
                  ),
                );
              }

              return;
            }

            if (hasSubscriptionAccessToStorePublishedContentsExtension) {
              setToggled(e.target.checked);
              setLoading(true);
              await createStorePublishedContentExtension(wsProjectUuid);
              setLoading(false);
              if (wsProjectUuid) {
                dispatch(getExtensionList(wsProjectUuid));
              }
            } else {
              dispatch(
                setPlanUpgradeNeededModalContext(
                  SubscriptionEntitlements.StorePublishedContentsExtension,
                ),
              );
            }
          }}
          disabled={
            !permissions?.project?.includes(
              ProjectPermissionsEnum.MANAGE_PROJECT_CONFIGURATION,
            ) || loading
          }
        />
      }
    />
  );
}
